import config from 'config'
import { useCallback, useEffect, useState } from 'react'

import ChiliColors from 'shared/components/Tools/services/ChiliColors'
import ChiliVariables from 'shared/components/Tools/services/ChiliVariables'
import { debounce } from 'shared/helpers/runtime'
import { ColorCMYK, CoverParts } from 'shared/types/global'
import { getChiliImageUrl } from 'shared/utils/rest'

const variablesApi = ChiliVariables.getInstance()
const colorsApi = ChiliColors.getInstance()

const PARTS = [CoverParts.back, CoverParts.spine, CoverParts.front]

export const useCoverBackgrounds = () => {
  const [imageUrls, setImageUrls] = useState<
    Record<CoverParts, string | null>
  >({
    [CoverParts.back]: null,
    [CoverParts.spine]: null,
    [CoverParts.front]: null
  })

  const [colors, setColors] = useState<
    Record<CoverParts, ColorCMYK | null>
  >({
    [CoverParts.back]: null,
    [CoverParts.spine]: null,
    [CoverParts.front]: null
  })

  useEffect(() => {
    if (!variablesApi.isInitialized) {
      return
    }

    fetchImages()
  }, [variablesApi.isInitialized])

  useEffect(() => {
    if (!colorsApi.isInitialized) {
      return
    }

    fetchColors()
  }, [colorsApi.isInitialized])

  const fetchImages = async () => {
    for (const part of PARTS) {
      const image = await variablesApi.getVariable(
        config.themes.variablesKeys.cover.background.image[part]
      )
      const xml = image?.imgXML

      let url: string | null = null

      if (xml) {
        const parser = new DOMParser()
        const xmlDoc = parser.parseFromString(xml, 'text/xml')
        const itemElement = xmlDoc.querySelector('item')
        const imageId = itemElement?.getAttribute('id')
        const path = itemElement?.getAttribute('path')

        url = getChiliImageUrl({
          id: imageId!,
          path: path!
        })
      }

      setImageUrls(prev => ({
        ...prev,
        [part]: url
      }))
    }
  }

  const fetchColors = async () => {
    for (const part of PARTS) {
      const colorName =
        config.themes.variablesKeys.cover.background.color[part]

      const color = await colorsApi.getColor(colorName)
      const cmyk = color?.color as ColorCMYK

      setColors(prev => ({
        ...prev,
        [part]: cmyk
      }))
    }
  }

  const debouncedColorChange = useCallback(
    debounce(async (part: CoverParts, color: ColorCMYK) => {
      // Color name is the same as the variable name
      const colorName =
        config.themes.variablesKeys.cover.background.color[part]

      await colorsApi.changeColor(
        `document.colors[${colorName}]`,
        color
      )
    }, 100),
    []
  )

  const handleColorChange = (part: CoverParts, color: ColorCMYK) => {
    debouncedColorChange(part, color)
  }

  return {
    backgroundColors: colors,
    backgroundImageUrls: imageUrls,
    refetchBackgroundImages: fetchImages,
    refetchBackgroundColors: fetchColors,
    handleBackgroundColorChange: handleColorChange
  }
}
